import React, { ChangeEvent, useEffect } from 'react';
import { Table, Form, Collapse, Alert, ListGroup, Spinner } from 'react-bootstrap';
import './PromotionProducts.scss';
import ImageThumbnail from 'components/ImageThumbnail';
import { AppState } from 'reducers';
import { useSelector } from 'react-redux';
import { DeliveryOption, PromotionalProduct, UserDeliveryAddress } from 'lib/types';
import ButtonComponent from 'components/ButtonComponent';
import { getUserDeliveryAddresses } from 'lib/communication/userInfo';
import DeliveryAddressSelect from 'components/DeliveryAddressSelect';
import permissionService from 'services';
import { useDevice } from 'lib/hooks/useDevice';

interface IPromotionProductsSummaryProps {
    grandTotalPoints: number;
    remainingPoints: number;
    selectedCount: Map<PromotionalProduct, number[]>;
    deliveryOption: DeliveryOption | null;
    setDeliveryOption: (_: DeliveryOption | null) => void;
    setShowSummary: (_: boolean) => void;
    submitNewOrder: (_: React.FormEvent<HTMLFormElement>) => void;
    addresses: UserDeliveryAddress[];
    deliveryAddress: UserDeliveryAddress | undefined;
    setDeliveryAddress: (address: UserDeliveryAddress | undefined) => void;
}

const PromotionProductsSummary = ({ grandTotalPoints, remainingPoints, selectedCount, deliveryOption, setDeliveryOption, setShowSummary, submitNewOrder, addresses, deliveryAddress, setDeliveryAddress }: IPromotionProductsSummaryProps) => {
    const { isDesktop } = useDevice();
    const delivery = useSelector((state: AppState) => state.data.promotionalProgram.delivery);
    const isAddingOrder = useSelector((state: AppState) => state.data.promotionalProgram.props.isAddingOrder);

    const handleDeliveryChange = (event: ChangeEvent<HTMLInputElement>) => {
        setDeliveryOption(event.currentTarget.value as DeliveryOption);
    };

    useEffect(() => {
        getUserDeliveryAddresses();
    }, []);

    return (
        <Form onSubmit={submitNewOrder}>
            <h3>Podsumowanie zamówienia</h3>
            {isDesktop ? (
                <Table borderless striped className='tr2 tr3 tr4'>
                    <thead>
                        <tr>
                            <th>Opis</th>
                            <th>Rozmiar</th>
                            <th>Ilość</th>
                            <th>Suma punktów</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from(selectedCount.entries()).flatMap(([product, counts]) => Array.from(counts.entries())
                            .filter(([_, value]) => value !== 0)
                            .map(([index, value]) => (
                                <tr key={`${product.symKar}_${index}_${value}`}>
                                    <td>{product.description}</td>
                                    <td>
                                        {product.options?.[index] !== '' ?
                                            product.options?.[index] :
                                            'N/D'}
                                    </td>
                                    <td>{value}</td>
                                    <td>{value * product.points}</td>
                                </tr>
                            )))}
                        {(deliveryOption === 'inputAddress') && (
                            <tr>
                                <td>{delivery.description}</td>
                                <td>N/D</td>
                                <td>1</td>
                                <td>{delivery.points}</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3} />
                            <td>{grandTotalPoints}</td>
                        </tr>
                    </tfoot>
                </Table>
            ) : (
                <>
                    <ListGroup variant='flush'>
                        {Array.from(selectedCount.entries()).flatMap(([product, counts]) => Array.from(counts.entries())
                            .filter(([_, value]) => value !== 0)
                            .map(([index, value]) => (
                                <ListGroup.Item key={`${product.symKar}_${index}_${value}`}>
                                    <p className='text-left clearfix'>
                                        <span className='PromotionProducts-photo-td float-left mr-3'>
                                            <ImageThumbnail
                                                src={product.photo}
                                                alt=''
                                            />
                                        </span>
                                        <span>{product.description}</span>
                                    </p>
                                    <div>
                                        <>
                                            Rozmiar:
                                            {' '}
                                            {product.options?.[index] !== '' ?
                                                product.options?.[index] :
                                                'N/D'}
                                            <br />
                                        </>
                                        Ilość:
                                        {' '}
                                        {value}
                                        <br />
                                        Suma punktów:
                                        {' '}
                                        {value * product.points}
                                    </div>
                                </ListGroup.Item>
                            )))}
                        {deliveryOption === 'inputAddress' && (
                            <ListGroup.Item key='deliveryItem'>
                                <p className='text-left clearfix'>
                                    <span>{delivery.description}</span>
                                </p>
                                <div>
                                    Rozmiar: N/D
                                    <br />
                                    Ilość: 1
                                    <br />
                                    Suma punktów:
                                    {' '}
                                    {delivery.points}
                                </div>
                            </ListGroup.Item>
                        )}
                    </ListGroup>
                    <p className='mt-3'>
                        <b>
                            Suma punktów zamówienia:
                            {grandTotalPoints}
                        </b>
                    </p>
                </>
            )}
            <div className='PromotionProducts-delivery mx-3 mb-3'>
                <h3>Dostawa</h3>
                {Array.from(selectedCount.entries()).flatMap(([product, counts]) => Array.from(counts.entries()).filter(([_, value]) => value !== 0).map((_) => product.isElectronicShipping)).includes(true) ? (
                    <Form.Check
                        type='radio'
                        label='Wysyłka elektroniczna'
                        checked
                        className='pb-3'
                    />
                ) : (
                    <>
                        {delivery.delivery === 'available' && (
                            <Form.Check
                                type='radio'
                                label={`${delivery.description}`}
                                name='delivery'
                                id='delivery-1'
                                value='inputAddress'
                                checked={deliveryOption === 'inputAddress'}
                                onChange={handleDeliveryChange}
                                className='pb-3'
                                required
                            />
                        )}
                        <Collapse in={deliveryOption === 'inputAddress'}>
                            <Form.Group controlId='DeliveryAddress'>
                                <DeliveryAddressSelect
                                    addresses={addresses}
                                    deliveryAddress={deliveryAddress}
                                    setDeliveryAddress={setDeliveryAddress}
                                />
                                <Form.Label className='NewOrder-desc-error'>&nbsp;</Form.Label>
                            </Form.Group>
                        </Collapse>
                        {
                            permissionService.haveAccessToOperation('changePromotionDeliveryAdress') ? (
                                <Form.Check
                                    type='radio'
                                    label='Wysyłka na adres dystrybutora'
                                    name='delivery'
                                    id='delivery-3'
                                    value='distributorAddress'
                                    checked={deliveryOption === 'distributorAddress'}
                                    onChange={handleDeliveryChange}
                                    className='pb-3'
                                    required
                                />
                            ) : null
                        }
                    </>
                )}
            </div>
            {remainingPoints < 0 && (
                <Alert variant='danger' className='my-3'>
                    Niewystarczająca ilość punktów.
                </Alert>
            )}
            <div id='PromotionProductsSummary-confirm-block'>
                <ButtonComponent
                    marginLeft='0'
                    marginRight='18px'
                    width='8em'
                    text='Wróć'
                    onHoverAnimation={3}
                    arrowDirection='left'
                    onClick={() => {
                        setDeliveryOption(null);
                        setShowSummary(false);
                        window.scrollTo(0, 0);
                    }}
                />
                {
                    isAddingOrder ? (
                        <Spinner animation='border' className='ml-5' />
                    ) : (
                        <ButtonComponent
                            marginLeft='0'
                            marginRight='0'
                            width='8em'
                            text='Zamów'
                            type='submit'
                            onHoverAnimation={3}
                            disabled={remainingPoints < 0}
                        />
                    )
                }
            </div>
        </Form>
    );
};

export default PromotionProductsSummary;
